import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  SigningSession,
  SigningSessionEndReason,
  SigningSessionEvent,
  SigningSessionEventType,
  SigningSessionStatus,
} from '@app/models/signing-session/signing-session';
import { SigningSessionProxyService } from '@app/proxies/signing-session.proxy.service';
import { SigningSessionStateService } from '@app/services/signing-session-state.service';
import { ToastService } from '@app/services/toast.service';
import { SigningSessionUtil } from '@app/utilities/signing-session-util';
import { finalize } from 'rxjs/operators';

type TagSeverity = 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast';

interface SessionCard {
  session: SigningSession;
  shortId: string;
  duration: string;
  statusLabel: string;
  severity: TagSeverity;
  reasonLabel: string;
  verificationMethod: string;
}

const EVENT_ICONS: Record<SigningSessionEventType, string> = {
  [SigningSessionEventType.Opened]: 'pi pi-sign-in',
  [SigningSessionEventType.OtpSent]: 'pi pi-send',
  [SigningSessionEventType.OtpVerified]: 'pi pi-verified',
  [SigningSessionEventType.OtpFailed]: 'pi pi-times-circle',
  [SigningSessionEventType.ActivityRegistered]: 'pi pi-pencil',
  [SigningSessionEventType.Expired]: 'pi pi-clock',
  [SigningSessionEventType.Closed]: 'pi pi-sign-out',
};

@Component({
  selector: 'app-session-history',
  templateUrl: './session-history.component.html',
  styleUrl: './session-history.component.scss',
})
export class SessionHistoryComponent {
  private readonly proxy = inject(SigningSessionProxyService);
  private readonly state = inject(SigningSessionStateService);
  private readonly translate = inject(TranslateService);
  private readonly toast = inject(ToastService);

  readonly sessions = signal<SigningSession[]>([]);
  readonly isLoading = signal(0);
  readonly expandedId = signal<string | null>(null);
  readonly events = signal<SigningSessionEvent[]>([]);
  readonly loadingEvents = signal(false);

  private readonly currentKey = computed(() => {
    const current = this.state.session();
    return current ? `${current.id}:${current.status}` : '';
  });

  readonly cards = computed<SessionCard[]>(() => {
    const live = this.state.session();
    const liveElapsed = this.state.elapsedSeconds();
    return this.sessions().map((session) => ({
      session,
      shortId: SigningSessionUtil.shortId(session.id),
      duration: SigningSessionUtil.formatClock(live?.id === session.id ? liveElapsed : this.durationOf(session)),
      statusLabel: this.statusLabel(session),
      severity: this.severityOf(session),
      reasonLabel: this.reasonLabel(session.endReason),
      // Rooms opened while codes still went by SMS or WhatsApp recorded the phone they went to.
      verificationMethod: session.userSnapshot?.phoneNumber
        ? `${this.translate.instant('Signing_History_Method_Message')} ${SigningSessionUtil.maskPhone(session.userSnapshot.phoneNumber)}`
        : 'Microsoft Authenticator',
    }));
  });

  constructor() {
    effect(
      () => {
        if (!this.state.loaded()) return;
        this.currentKey();
        untracked(() => this.load());
      },
      { allowSignalWrites: true },
    );
    this.state.ensureLoaded();
  }

  toggle(event: Event, id: string): void {
    event.preventDefault();
    if (this.expandedId() === id) {
      this.expandedId.set(null);
      return;
    }

    this.expandedId.set(id);
    this.events.set([]);
    this.loadingEvents.set(true);
    this.proxy
      .getEvents(id)
      .pipe(finalize(() => this.loadingEvents.set(false)))
      .subscribe({
        next: (response) => this.events.set(response.data ?? []),
        error: (error) => this.toast.error(this.translate.instant('Signing_Error_Title'), this.state.messageFrom(error)),
      });
  }

  eventIcon(type: SigningSessionEventType): string {
    return EVENT_ICONS[type] ?? 'pi pi-circle';
  }

  eventLabel(type: SigningSessionEventType): string {
    return this.translate.instant(`Signing_Event_${SigningSessionEventType[type]}`);
  }

  eventDetail(event: SigningSessionEvent): string {
    const meta = this.parseMetadata(event.metadata);
    switch (event.eventType) {
      case SigningSessionEventType.OtpSent: {
        const resend = Number(meta['resend'] ?? 0);
        const label =
          resend > 0
            ? `${this.translate.instant('Signing_Event_Detail_Resent')} #${resend}`
            : this.translate.instant('Signing_Event_Detail_Sent');
        // Codes sent before the move to WhatsApp carry no channel; they all went by SMS.
        const channel = (meta['channel'] as string | undefined) ?? 'SMS';
        return [label, channel, meta['phone']].filter(Boolean).join(' · ');
      }
      case SigningSessionEventType.OtpVerified:
        return meta['method'] === 'Authenticator' ? 'Microsoft Authenticator' : '';
      case SigningSessionEventType.OtpFailed:
        return `${this.translate.instant('Signing_Event_Detail_Attempt')} ${meta['attempt']} · ${meta['remaining']} ${this.translate.instant('Signing_Otp_AttemptsLeft')}`;
      case SigningSessionEventType.Expired:
      case SigningSessionEventType.Closed: {
        const reason = SigningSessionEndReason[meta['reason'] as keyof typeof SigningSessionEndReason];
        return reason === undefined ? '' : this.reasonLabel(reason);
      }
      default:
        return '';
    }
  }

  private load(): void {
    this.isLoading.update((v) => v + 1);
    this.proxy
      .getHistory(50)
      .pipe(finalize(() => this.isLoading.update((v) => v - 1)))
      .subscribe({
        next: (response) => this.sessions.set(response.data ?? []),
        error: (error: HttpErrorResponse) => {
          if (error.status === 404) {
            this.sessions.set([]);
            return;
          }
          this.toast.error(this.translate.instant('Signing_Error_Title'), this.state.messageFrom(error));
        },
      });
  }

  private durationOf(session: SigningSession): number {
    if (session.endedAt) return (Date.parse(session.endedAt) - Date.parse(session.openedAt)) / 1000;
    return session.hardCapSeconds - session.secondsUntilHardExpiry;
  }

  private statusLabel(session: SigningSession): string {
    switch (session.status) {
      case SigningSessionStatus.Open:
        return this.translate.instant('Signing_Status_Pending');
      case SigningSessionStatus.Verified:
      case SigningSessionStatus.Active:
        return this.translate.instant('Signing_Status_Active');
      case SigningSessionStatus.Expired:
        return this.translate.instant('Signing_Status_Expired');
      default:
        return this.translate.instant('Signing_Status_Closed');
    }
  }

  private severityOf(session: SigningSession): TagSeverity {
    switch (session.status) {
      case SigningSessionStatus.Open:
        return 'warn';
      case SigningSessionStatus.Verified:
      case SigningSessionStatus.Active:
        return 'info';
      case SigningSessionStatus.Expired:
        return 'secondary';
      default:
        return session.endReason === SigningSessionEndReason.UserClosed ? 'success' : 'danger';
    }
  }

  private reasonLabel(reason: SigningSessionEndReason | undefined | null): string {
    if (reason === undefined || reason === null) return '—';
    return this.translate.instant(`Signing_Reason_${SigningSessionEndReason[reason]}`);
  }

  private parseMetadata(metadata: string | undefined): Record<string, unknown> {
    if (!metadata) return {};
    try {
      return JSON.parse(metadata) as Record<string, unknown>;
    } catch {
      return {};
    }
  }
}
